import React from "react";
import { Button as ButtonAntd, ConfigProvider, Popover, Tooltip } from "antd";
import { MoreOutlined } from "@ant-design/icons";
import { useWidth } from "@hooks";

interface ToolbarActionsResponsiveProps {
  children: React.ReactNode;
  /** Texto del título del popover en mobile. */
  label?: string;
  /**
   * Forzar el modo agrupado sin importar el ancho. Útil para tablas con
   * muchísimos botones que también se apretujan en desktop.
   */
  forceGroup?: boolean;
}

/**
 * Props compartidas para el campo de búsqueda de las tablas MRT
 * (`muiSearchTextFieldProps`). El ancho lo controla `.mrt-search-field`
 * en `src/index.css`, que lo achica en mobile para que el toolbar entre
 * en una sola fila.
 */
export const mrtSearchTextFieldProps = {
  placeholder: "Buscar...",
  size: "small" as const,
  variant: "outlined" as const,
  className: "mrt-search-field",
};

/**
 * Envuelve los botones del `renderTopToolbarCustomActions` de las tablas MRT.
 *
 * - Desktop (md+): renderiza los botones tal cual, en línea.
 * - Mobile (xs / sm): los agrupa en un botón cuadrado (solo ícono, misma
 *   altura que el campo de búsqueda) que abre un popover corporativo con los
 *   botones apilados en columna. Así el toolbar deja de empujar la tabla y la
 *   paginación vuelve a verse.
 */
export const ToolbarActionsResponsive = ({
  children,
  label = "Acciones",
  forceGroup = false,
}: ToolbarActionsResponsiveProps) => {
  const width = useWidth();
  const isMobile = forceGroup || width === "xs" || width === "sm";

  if (!isMobile) return <>{children}</>;

  return (
    <ConfigProvider
      theme={{
        components: {
          Popover: { zIndexPopup: 1400 },
        },
      }}
    >
      <Popover
        trigger="click"
        placement="bottomLeft"
        getPopupContainer={() => document.body}
        title={
          <span style={{ fontWeight: 600, fontSize: 13, letterSpacing: 0.2 }}>
            {label}
          </span>
        }
        content={
          <div className="mrt-toolbar-actions-popover">{children}</div>
        }
      >
        <Tooltip title={label}>
          <ButtonAntd
            aria-label={label}
            icon={<MoreOutlined style={{ fontSize: 18 }} />}
            className="mrt-toolbar-actions-trigger"
          />
        </Tooltip>
      </Popover>
    </ConfigProvider>
  );
};
